import type { ReactElement } from 'react'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'
import { Layout } from '@/components/enlayouts/Ir'
import { BulletedList } from '@/components/enlayouts/Ir/BulletedList'
import { BHeading } from '@/components/enlayouts/Ir/Heading'
import { Paragraph } from '@/components/enlayouts/Ir/Paragraph'

export const IssuePage = () => {
  return (
    <Layout>
      <SectionTitle color="normal" enTitleText="Issues" subTitleText="" />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <Paragraph>
          The Group aims to expand the store network through enhancing the
          attractiveness and profitability of stores through strengthening food
          offerings with a focus on fresh food, as well as store renovations in
          the retail distribution business. Additionally, in the Retail AI
          business, the Group strives to improve the value proposition to retail
          companies, food and consumer goods manufacturers, and vendors through
          the development of new products and solutions, as well as the
          enhancement of existing products. Although the Group continues to
          operate in a challenging business environment, including rising
          consumer demand for savings amid higher prices driven by increases in
          energy costs and personnel expenses, we will continue to build a
          business foundation for sustainable growth over the medium to long
          term.
        </Paragraph>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          Through the full acquisition of SEIYU on July 1, 2025, the Group
          significantly expanded its business scale and operating base. At the
          same time, key management challenges include integrating the resources
          of the two companies, realizing group synergies at an early stage,
          improving the profitability of existing businesses, maintaining
          financial soundness, and establishing a management structure capable
          of supporting the Group’s expanded scale.
        </Paragraph>
        <Paragraph>
          The Group formulated its Medium-Term Management Plan in February 2026,
          positioning the three-year period from FY6/2027 through FY6/2029 as
          “Three Years to Build the Foundation for Retail Transformation” toward
          realizing its long-term vision.
        </Paragraph>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          The Group has set quantitative targets for FY6/2029 of 1.6 trillion
          yen in net sales, 100.0 billion yen in EBITDA, and 64.0 billion yen in
          operating profit. By strengthening the cash-generating capabilities
          centered on synergies, we will improve financial soundness and build a
          strong foundation for significant growth from FY6/2030 onward.
        </Paragraph>
        <Paragraph>
          To achieve these targets, the Group has identified the following five
          key strategies and will prioritize their execution.
        </Paragraph>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>① Maximizing Group Synergies Through PMI</BHeading>
        <Paragraph>
          To execute the integration process with SEIYU swiftly and steadily to
          improve profitability and productivity across the Group.
        </Paragraph>
        <BulletedList>
          <li>Optimization of Procurement and Sourcing: Standardize procurement terms and consolidate suppliers to generate gross margin improvement.</li>
          <li>Strengthening PB and Advancing Category Management: Reorganize, cross-sell, and jointly develop the two companies’ brands, including “Kohaku Hompo” and “Minasama no Osumitsuki,” with the aim of increasing the Group-wide PB sales mix to 25%. At the same time, we will improve shelf productivity through AI and data analytics and integrate the operations of Process Centers and Central Kitchens.</li>
          <li>Promoting System Integration: Integrate and consolidate SEIYU’s core systems into TRIAL’s systems to reduce development, operation, and maintenance costs.</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>
          ② Enhancing TRIAL’s Profitability and Driving Growth
        </BHeading>
        <Paragraph>
          Drive sustainable growth by refining the high-profitability business
          model and strategically expanding the store network.
        </Paragraph>
        <BulletedList>
          <li>Accelerating Store Openings: By leveraging the proprietary trade area analysis tool, “Retail Map,” we will open 35 Supercenters over three years based on highly accurate site selection. We will also accelerate the multi-store rollout of the small-format “TRIAL GO,” targeting 100 stores over three years.</li>
          <li>Sustaining Same-Store Sales Growth: Actively renovate existing stores, with a cumulative total of 45 stores over three years, while strengthening the food and PB offerings, targeting an 8% sales mix for ready meals.</li>
          <li>Enhancing the Shopping Experience Through Digital Technologies: In addition to refining the pricing, we will leverage Skip Cart, in-store signage, membership app, and other digital tools to deliver personalized approaches based on individual customer needs, achieving both price-focused and value-focused offerings.</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>
          ③ Transforming SEIYU Stores and Expanding Customer Support
        </BHeading>
        <Paragraph>
          Undertake fundamental store reforms at SEIYU to drive top-line growth
          and earn stronger customer traffic.
        </Paragraph>
        <BulletedList>
          <li>Rolling Out Successful Merchandising and Renovation Models: Roll out the know-how developed at successful model stores across the entire network to strengthen existing stores, while strategically renovating 60 Supermarkets.</li>
          <li>Expanding the New “TRIAL SEIYU” Format: Convert 30 large stores (Hypermarkets) into “TRIAL SEIYU” format, a successful fresh-food-focused store model with price competitiveness as a key strength.</li>
          <li>Reducing the SG&A Ratio: While proactive price investments will temporarily lower the gross profit margin, we will build a structure for significant SG&A ratio reductions and profit growth through fixed-cost absorption from higher sales and the benefits of system integration and other initiatives.</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>
          ④ Transitioning the DX Strategy to the Monetization Phase
        </BHeading>
        <Paragraph>
          Move the DX foundation built to date into full-scale monetization and
          support growth across the Group.
        </Paragraph>
        <BulletedList>
          <li>Group-wide Utilization of Data and AI: Alongside the integration of core systems, promote the use of data and AI across the entire Group, including SEIYU.</li>
          <li>Building New Business Platforms: Make concrete progress in building the business platforms for retail media and e-commerce and establish capabilities that will contribute to future sales and profit growth.</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <BHeading>
          ⑤ Strengthening the Management Foundation to Support Sustainable
          Growth
        </BHeading>
        <Paragraph>
          Achieve an optimal balance between growth investments and financial
          soundness to maximize corporate value.
        </Paragraph>
        <BulletedList>
          <li>Promoting Sustainability Management: Guided by the Group’s purpose, “Enrich every aspect of people&apos;s lives through all we do.” contributes to the development and growth of both the Group and society as a whole. Centered on the operation of physical stores, pursue a sustainable society through the advancement of Retail DX and collaboration with diverse partners.</li>
          <li>Appropriate Cash Allocation: Using cash generated from operating activities as the primary source of funding, prioritize the allocation of 130.0 billion yen to growth investments, including new store openings, format conversions, and store renovations.</li>
          <li>Restoring Financial Soundness: Execute disciplined debt repayment following the full acquisition of SEIYU, targeting 115.0 billion yen in debt repayment over three years, with the aim of achieving a Net Debt/EBITDA ratio of 3.0x or below and an equity ratio of 20% or higher by FY6/2029.</li>
          <li>Human Capital, Partnerships, and Shareholder Returns: In addition to strengthening human capital and building external partnerships that contribute to transformation across the industry, we will consider strategic M&A opportunities. For shareholders, we will provide stable and sustainable returns based on our progressive dividend policy.</li>
        </BulletedList>

        <Spacer h={{ sp: 30, pc: 50 }} />
        <Paragraph>
          The Group will pursue these key strategies as one team and establish
          and roll out successful models for next-generation smart stores, with
          the aim of making a significant leap toward becoming a world-leading
          “Retail Platformer.”
        </Paragraph>
      </section>
    </Layout>
  )
}

IssuePage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Investor Relations', path: '/en/ir/' },
    { name: 'Management Policy', path: '/en/ir/manage' },
    { name: 'Issues', path: '' },
  ]

  return (
    <>
      <Head
        title="Issues"
        description="Business issues that should be addressed on a priority basis."
        ogp={{ url: 'en/ir/manage/issue/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle=""
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}
